import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Gauge, Maximize, Minimize, Pause, PictureInPicture2, Play, Volume2, VolumeX } from "lucide-react";
import "./RoutineVideoPlayer.css";

const SPEEDS = [2, 1.5, 1.25, 1, 0.75, 0.5];
const timeLabel = (seconds: number) => {
  const total = Math.floor(Number.isFinite(seconds) ? seconds : 0);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

export const RoutineVideoPlayer = forwardRef<HTMLVideoElement, {
  src: string;
  onPause: () => void;
  onEnded: () => void;
  onError: () => void;
}>(function RoutineVideoPlayer({ src, onPause, onEnded, onError }, ref) {
  const video = useRef<HTMLVideoElement | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const speedButton = useRef<HTMLButtonElement>(null);
  const speedMenu = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const restore = useRef<{ time: number; playing: boolean } | null>(null);
  const playbackIntent = useRef(false);
  const attachVideo = useCallback((element: HTMLVideoElement | null) => {
    video.current = element;
    if (typeof ref === "function") ref(element);
    else if (ref) ref.current = element;
  }, [ref]);

  useLayoutEffect(() => {
    const element = video.current;
    if (!element || element.getAttribute("src") === src) return;
    // Capture before assigning src: the browser resets its playhead on reload.
    // Keep the original position if another renewal arrives while still loading.
    if (element.getAttribute("src") && !restore.current)
      restore.current = { time: element.currentTime, playing: playbackIntent.current };
    element.src = src;
  }, [src]);

  useEffect(() => {
    if (!video.current) return;
    video.current.defaultPlaybackRate = speed;
    video.current.playbackRate = speed;
    video.current.preservesPitch = true;
  }, [speed, src]);

  useEffect(() => {
    const update = () => setIsFullscreen(document.fullscreenElement === container.current);
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    speedMenu.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    const dismiss = (event: PointerEvent) => {
      if (!speedMenu.current?.contains(event.target as Node) &&
          !speedButton.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [menuOpen]);

  const togglePlayback = async () => {
    if (!video.current) return;
    setError("");
    if (!video.current.paused) video.current.pause();
    else {
      try { await video.current.play(); }
      catch { setError("Playback could not start. Please try again."); }
    }
  };
  const fullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (container.current?.requestFullscreen) await container.current.requestFullscreen();
      else (video.current as HTMLVideoElement & { webkitEnterFullscreen?: () => void })?.webkitEnterFullscreen?.();
    } catch { setError("Fullscreen is unavailable in this browser."); }
  };
  const pictureInPicture = async () => {
    if (!video.current) return;
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture();
      else if (document.pictureInPictureEnabled) await video.current.requestPictureInPicture();
      else {
        const safariVideo = video.current as HTMLVideoElement & {
          webkitSetPresentationMode?: (mode: string) => void;
          webkitPresentationMode?: string;
        };
        if (safariVideo.webkitSetPresentationMode)
          safariVideo.webkitSetPresentationMode(safariVideo.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture");
        else setError("Picture-in-picture is unavailable in this browser.");
      }
    } catch { setError("Picture-in-picture is unavailable in this browser."); }
  };
  const iconButton = "flex h-8 w-6 shrink-0 items-center justify-center rounded text-white hover:text-[#c87bb0] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

  return (
    <div ref={container} style={{ containerType: "inline-size" }} className="relative flex aspect-video w-full items-center justify-center bg-black text-white">
      <video
        ref={attachVideo}
        playsInline
        controlsList="nodownload"
        preload="metadata"
        className="h-full w-full object-contain"
        onClick={() => void togglePlayback()}
        onContextMenu={(event) => event.preventDefault()}
        onLoadedMetadata={(event) => {
          const element = event.currentTarget;
          const saved = restore.current;
          restore.current = null;
          element.playbackRate = speed;
          if (saved) {
            element.currentTime = Math.min(saved.time, element.duration);
            if (saved.playing) void element.play().catch(() => {
              setError("Press Play to continue from your saved position.");
            });
          }
          setDuration(element.duration);
          setPosition(element.currentTime);
          setError("");
        }}
        onTimeUpdate={(event) => setPosition(event.currentTarget.currentTime)}
        onPlay={() => { playbackIntent.current = true; setPlaying(true); }}
        onPause={() => {
          if (restore.current || video.current?.error) return;
          playbackIntent.current = false;
          setPlaying(false);
          onPause();
        }}
        onEnded={() => { playbackIntent.current = false; setPlaying(false); onEnded(); }}
        onVolumeChange={(event) => {
          setMuted(event.currentTarget.muted || event.currentTarget.volume === 0);
          setVolume(event.currentTarget.volume);
        }}
        onRateChange={(event) => setSpeed(event.currentTarget.playbackRate)}
        onError={onError}
      />
      {!playing && (
        <button type="button" aria-label="Play video" onClick={() => void togglePlayback()}
          className="absolute left-1/2 top-1/2 flex h-10 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded bg-black/80 text-white hover:bg-[#762a5c] focus-visible:outline-2 focus-visible:outline-white">
          <Play size={23} fill="currentColor" />
        </button>
      )}
      {error && <p role="alert" className="absolute left-3 right-3 top-3 rounded bg-black/85 p-2 text-xs">{error}</p>}
      <div className="absolute inset-x-2 bottom-2 flex items-center gap-1 rounded bg-[#191919]/95 px-2 py-1" aria-label="Video controls">
        <button type="button" aria-label={playing ? "Pause" : "Play"} onClick={() => void togglePlayback()} className={iconButton}>
          {playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
        </button>
        <span className="text-[10px] tabular-nums">{timeLabel(position)}</span>
        <input type="range" aria-label="Video progress" min={0} max={duration || 0} step={0.1} value={Math.min(position, duration || 0)}
          style={{ background: `linear-gradient(to right, #b8649e ${duration ? position / duration * 100 : 0}%, #666 ${duration ? position / duration * 100 : 0}%)` }}
          onChange={(event) => {
            if (!video.current || !duration) return;
            video.current.currentTime = Number(event.target.value);
            setPosition(Number(event.target.value));
          }} className="routine-video-progress min-w-0 flex-1 cursor-pointer" />
        <span className="hidden text-[10px] tabular-nums sm:inline">{timeLabel(duration)}</span>
        <div className="group/volume relative">
        <button type="button" aria-label={muted ? "Unmute" : "Mute"} onClick={() => {
          if (!video.current) return;
          video.current.muted = !muted;
          if (muted && video.current.volume === 0) video.current.volume = 1;
        }} className={iconButton}>{muted ? <VolumeX size={19} /> : <Volume2 size={19} />}</button>
        <div className="absolute bottom-full left-1/2 hidden -translate-x-1/2 pb-2 group-hover/volume:block group-focus-within/volume:block">
          <div className="flex h-24 w-9 justify-center rounded bg-[#333] py-3">
            <input type="range" aria-label="Volume" min={0} max={1} step={0.05} value={muted ? 0 : volume}
              style={{ writingMode: "vertical-lr", direction: "rtl" }}
              className="w-1 cursor-pointer accent-[#b8649e]" onChange={(event) => {
                if (!video.current) return;
                video.current.volume = Number(event.target.value);
                video.current.muted = Number(event.target.value) === 0;
              }} />
          </div>
        </div>
        </div>
        <div className="relative" onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setMenuOpen(false);
        }}>
          <button ref={speedButton} type="button" aria-label="Speed" title="Speed" aria-haspopup="menu" aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)} className={`${iconButton} ${menuOpen || speed !== 1 ? "!text-[#c87bb0]" : ""}`}>
            <Gauge size={19} />
          </button>
          {menuOpen && (
            <div ref={speedMenu} role="menu" aria-label="Playback speed"
              style={{ maxHeight: "calc(56.25cqw - 60px)" }}
              className="absolute bottom-10 right-0 z-10 w-40 overflow-y-auto rounded-lg bg-[#333] py-1 text-sm shadow-lg"
              onKeyDown={(event) => {
                const options = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button'));
                const index = options.indexOf(document.activeElement as HTMLButtonElement);
                if (event.key === "Escape") { event.preventDefault(); setMenuOpen(false); speedButton.current?.focus(); }
                else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
                  event.preventDefault();
                  const next = event.key === "Home" ? 0 : event.key === "End" ? options.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length;
                  options[next]?.focus();
                }
              }}>
              {SPEEDS.map((rate) => (
                <button key={rate} type="button" role="menuitemradio" aria-checked={speed === rate}
                  onClick={() => { setSpeed(rate); setMenuOpen(false); speedButton.current?.focus(); }}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-white hover:bg-white/10 focus-visible:bg-white/10 focus:outline-none">
                  <span className="w-4">{speed === rate && <Check size={15} />}</span> {rate}x
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" aria-label="Picture-in-Picture" title="Picture-in-Picture" onClick={() => void pictureInPicture()} className={iconButton}><PictureInPicture2 size={18} /></button>
        <button type="button" aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"} onClick={() => void fullscreen()} className={iconButton}>{isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}</button>
      </div>
    </div>
  );
});
